import {
  ThemeProvider,
  accordionSummaryClasses,
  autocompleteClasses,
  outlinedInputClasses,
} from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { type ReactNode } from 'react';

import { colors, muiTheme } from '@vivaahealth/design-system';

export const CUSTOM_SCROLL_BAR_SIZE_IN_PIXELS = 8;
export const CUSTOM_BORDER_RADIUS_IN_PIXELS = 4;
export const NAVIGATION_HORIZONTAL_PADDING = 16;
const DARK_THEME_FONT_COLOR = '#F0F1FF';

export const customScrollbarStyles = {
  '&::-webkit-scrollbar': {
    width: CUSTOM_SCROLL_BAR_SIZE_IN_PIXELS,
    height: CUSTOM_SCROLL_BAR_SIZE_IN_PIXELS,
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: '4px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.4)' },
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
};

const focusVisibleRing = {
  outline: '2px solid',
  outlineColor: colors.extended.blue[30],
  outlineOffset: '2px',
};
const opacityHoverStyles = {
  opacity: 1,
  '&:hover': {
    opacity: 0.9,
  },
};

const darkTheme = createTheme({
  typography: deepmerge(muiTheme.typography, {
    allVariants: {
      color: DARK_THEME_FONT_COLOR,
    },
    h1: {
      color: DARK_THEME_FONT_COLOR,
    },
    h2: {
      color: DARK_THEME_FONT_COLOR,
    },
    h3: {
      color: DARK_THEME_FONT_COLOR,
    },
    h4: {
      color: DARK_THEME_FONT_COLOR,
    },
    h5: {
      color: DARK_THEME_FONT_COLOR,
    },
    h6: {
      color: DARK_THEME_FONT_COLOR,
    },
    body1: {
      color: DARK_THEME_FONT_COLOR,
    },
    body2: {
      color: DARK_THEME_FONT_COLOR,
    },
    caption: {
      color: DARK_THEME_FONT_COLOR,
    },
    code: {
      color: DARK_THEME_FONT_COLOR,
    },
    codeSmall: {
      color: DARK_THEME_FONT_COLOR,
    },
  }),

  palette: {
    background: {
      default: colors.extended.gray[25],
      paper: colors.extended.blue[10],
    },
    primary: {
      light: colors.extended.blue[10],
      main: colors.extended.blue[60],
      dark: colors.extended.blue[90],
      contrastText: DARK_THEME_FONT_COLOR,
    },
    text: {
      primary: DARK_THEME_FONT_COLOR,
      secondary: DARK_THEME_FONT_COLOR,
    },
    divider: '#282E54',
  },
  components: {
    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
        square: true,
      },
      styleOverrides: {
        root: {
          background: 'transparent',
          boxShadow: 'none',
          [`.${accordionSummaryClasses.focusVisible}`]: focusVisibleRing,
          '&::before': {
            display: 'none',
          },
        },
        gutters: {
          '&.Mui-expanded': {
            marginTop: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: CUSTOM_BORDER_RADIUS_IN_PIXELS,
          minHeight: 32,
          padding: `0 ${NAVIGATION_HORIZONTAL_PADDING - CUSTOM_SCROLL_BAR_SIZE_IN_PIXELS}px`,
        },
        content: {
          ...opacityHoverStyles,
          margin: '4px 0',
          [`&.${accordionSummaryClasses.expanded}`]: {
            margin: '4px 0',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: `0 ${NAVIGATION_HORIZONTAL_PADDING - CUSTOM_SCROLL_BAR_SIZE_IN_PIXELS}px`,
          marginBottom: '8px',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: {
          fontSize: '14px',
          paddingLeft: '12px',
          paddingRight: '12px',
          marginLeft: '8px',
          marginRight: '8px',
          borderRadius: CUSTOM_BORDER_RADIUS_IN_PIXELS,
          [`&.${autocompleteClasses.focused}`]: {
            backgroundColor: 'rgba(255, 255, 255, 0.2) !important',
          },
          [`&.${autocompleteClasses.focusVisible}`]: focusVisibleRing,
        },
        listbox: {
          backgroundColor: '#030A35',
        },
        input: {
          height: 'auto',
        },
        inputRoot: {
          [`&.${outlinedInputClasses.sizeSmall}`]: {
            padding: '2px 4px',
          },
          [`&.${outlinedInputClasses.sizeSmall} .${autocompleteClasses.input}`]: {
            paddingLeft: 0,
          },
        },
        root: {
          paddingTop: '4px',
          paddingBottom: '4px',
          [`& .${outlinedInputClasses.root}`]: {
            [`.${outlinedInputClasses.notchedOutline}`]: { border: 'none' },
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            [`&.${outlinedInputClasses.focused}`]: { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          '&:focus': focusVisibleRing,
          ...opacityHoverStyles,
          '&:hover': {
            ...opacityHoverStyles['&:hover'],
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          '&:focus': focusVisibleRing,
          ...opacityHoverStyles,
          '&:hover': {
            ...opacityHoverStyles['&:hover'],
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          // Override bootstrap.
          opacity: 1,
        },
      },
    },
    MuiInputBase: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        input: {
          fontSize: '1rem',
          padding: '4.5px 0',
          height: '1.5em',
          '&::placeholder': {
            color: colors.extended.gray[97],
            opacity: 0.9,
            fontStyle: 'italic',
          },
          '&:-webkit-autofill': {
            // This overrides the yellow background on browser native autofill
            WebkitBoxShadow: `0 0 0px 1000px ${colors.extended.blue[10]} inset`,
            WebkitTextFillColor: colors.common.white,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          borderRadius: CUSTOM_BORDER_RADIUS_IN_PIXELS,
          '&:focusVisible': focusVisibleRing,
          ...opacityHoverStyles,
          '&:hover': {
            ...opacityHoverStyles['&:hover'],
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: colors.extended.gray[35],
        },
      },
    },
  },
});

interface NavigationDrawerThemeProps {
  children: ReactNode;
}

export function NavigationDrawerTheme(props: NavigationDrawerThemeProps) {
  const { children } = props;
  return <ThemeProvider theme={darkTheme}>{children}</ThemeProvider>;
}
